import React from 'react'
import {
  CuratedItemsForLeadComponent,
  CategoryInfoFragment,
  MostRecentLeadFragment,
  DepartmentTopLevelCategoryFragment,
  DepartmentType,
  HeroExpertCuratedItemImageFragment,
  ExpertCuratedItemsForLeadChangedComponent
} from '#src/generated/graphql'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import NoLead from './NoLead'
import StatusBar from './StatusBar'

export interface SellableWithImage {
  id: string
  title: string
  url: string
}

export interface CategoryItemsBadge {
  categoryName: string
  itemsNumber: number
  hasNotes?: boolean
}

interface CuratedStatusBarProps {
  lead?: MostRecentLeadFragment | null
  topLevelCategory: CategoryInfoFragment | DepartmentTopLevelCategoryFragment | null
  departmentSlug: string
  isDepartmentInactive: boolean
  hideCurations?: boolean
}

const CuratedStatusBarComponent: React.SFC<CuratedStatusBarProps & WithAnalyticsProps> = ({
  lead,
  analytics,
  topLevelCategory,
  departmentSlug,
  isDepartmentInactive,
  hideCurations
}) => {
  if (hideCurations || !lead) {
    return (
      <NoLead
        topLevelCategory={topLevelCategory}
        departmentSlug={departmentSlug}
        isDepartmentInactive={isDepartmentInactive}
      />
    )
  }

  const leadId = lead.id
  const conversationId = lead.conversation.id

  /**
   * While this is just used on the home page, a query will work fine. If this follows
   * you around the rest of the site it should update to be a subscription. @ktsosno
   * This query is similar to `ExpertCuratedItemsForLead`, worth checking if they can be merged
   */
  return (
    <CuratedItemsForLeadComponent
      variables={{
        leadId
      }}
    >
      {({ data, refetch }) => {
        if (data && data.expertCuratedItemsForLead && data.lead) {
          const leadDepartmentType = data.lead.department && data.lead.department.type

          // Compose curation link based on type or curated orders
          const recommendationsLink =
            leadDepartmentType === DepartmentType.YACHT_CHARTERS
              ? `/curations/experiences/review/${leadId}`
              : `/curations/products/${leadId}`

          // Get item images either from curated orders or lead items in form of sellables
          const itemsSellables: SellableWithImage[] = data.expertCuratedOrdersForLeadConnection
            .edges.length
            ? data.expertCuratedOrdersForLeadConnection.edges
                .map(
                  ({
                    node: {
                      order: { lineItems }
                    }
                  }) => {
                    const parentLineItems = lineItems.filter(({ isGroup }) => isGroup)
                    const flagshipLineItem = parentLineItems[0]
                    if (
                      !flagshipLineItem ||
                      flagshipLineItem.source.__typename !== 'OrderLineItemSellableSource'
                    ) {
                      return { id: '', title: '', url: '' }
                    }
                    const { id, title, images } = flagshipLineItem.source.sellable
                    return {
                      id,
                      title,
                      url: images[0] && images[0].url
                    }
                  }
                )
                .filter(({ id, url }) => id && url)
            : data.expertCuratedItemsForLead.items
                .map(({ sellable: { id, title, images } }) => ({
                  id,
                  title,
                  url: images[0] && images[0].url
                }))
                .filter(({ url }) => url)

          // Get each category items from curated orders or lead items in form of sellables
          const categories: CategoryItemsBadge[] = data.expertCuratedOrdersForLeadConnection.edges
            .length
            ? [
                {
                  categoryName: data.expertCuratedOrdersForLeadConnection.edges[0].node.lead
                    .category!.singularDisplayName,
                  itemsNumber: data.expertCuratedOrdersForLeadConnection.edges.length
                }
              ]
            : toCategoryMap(data.expertCuratedItemsForLead.items)

          const messageCount =
            (data.lead.consumerConversationMetrics.messageCount || 0) +
            (data.lead.expertConversationMetrics.messageCount || 0)

          const itemCount = Math.max(
            data.expertCuratedItemsForLead.items.length,
            itemsSellables.length
          )

          const banner = topLevelCategory?.contentAssets?.communityPoster?.imageUrl

          const onSubmit = () => {
            if (analytics) analytics.events.curatedStatusBarViewCurationClicked()
          }
          return (
            <>
              <ExpertCuratedItemsForLeadChangedComponent
                variables={{ leadId: leadId }}
                onSubscriptionData={({ subscriptionData: {} }) => {
                  refetch()
                }}
              />
              <StatusBar
                categories={categories}
                itemsSellables={itemsSellables}
                onSubmit={onSubmit}
                messageCount={messageCount}
                itemCount={itemCount}
                banner={banner}
                expert={data.lead.expert}
                recommendationsLink={recommendationsLink}
                departmentType={leadDepartmentType}
                conversationId={conversationId}
              />
            </>
          )
        } else {
          return null
        }
      }}
    </CuratedItemsForLeadComponent>
  )
}

const toCategoryMap = (items: HeroExpertCuratedItemImageFragment[]) => {
  const itemByCategoryList = [] as CategoryItemsBadge[]
  items.forEach(item => {
    const itemByCategory = itemByCategoryList.find(
      v => v.categoryName === item.category.singularDisplayName
    )
    if (itemByCategory) {
      itemByCategory.itemsNumber++
      itemByCategory.hasNotes = itemByCategory.hasNotes || !!item.note
    } else {
      itemByCategoryList.push({
        itemsNumber: 1,
        categoryName: item.category.singularDisplayName
      })
    }
  })
  return itemByCategoryList
}

export default withAnalytics(CuratedStatusBarComponent)
